import { ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import logoUrl from '@/assets/ardinotech-logo.png';
import { brand } from '@/theme/theme';

interface Props {
  /** Renders nav links / tagline in the centre. Optional. */
  center?: ReactNode;
  /**
   * What to show on the right side. Defaults to the public-page combo of
   * Login (text) + Register (CTA). Pass a custom node for in-app variants.
   */
  right?: ReactNode;
  /**
   * Override the small label that sits on top of the big "Interface" word.
   * Defaults to "Ardinotech's".
   */
  ownerLabel?: string;
  /** Override the large product word. Defaults to "Interface". */
  productName?: string;
}

const shine = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export function GlassHeader({
  center,
  right,
  ownerLabel = "Ardinotech's",
  productName = 'Interface',
}: Props) {
  const navigate = useNavigate();

  const defaultRight = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Button
        onClick={() => navigate('/login')}
        sx={{
          color: brand.navy,
          fontWeight: 600,
          '&:hover': { background: 'rgba(11, 21, 48, 0.04)' },
        }}
      >
        Login
      </Button>
      <Button
        variant="contained"
        onClick={() => navigate('/register-tenant')}
        sx={{ minWidth: 110 }}
      >
        Register
      </Button>
    </Stack>
  );

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        height: { xs: 41, md: 53 },
        px: { xs: 2, md: 4 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backdropFilter: 'blur(18px) saturate(180%)',
        WebkitBackdropFilter: 'blur(18px) saturate(180%)',
        background: brand.glassBg,
        boxShadow: '0 2px 12px -8px rgba(11, 21, 48, 0.12)',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, ${brand.navy} 0%, ${brand.violet} 35%, ${brand.violetSoft} 50%, ${brand.violet} 65%, ${brand.navy} 100%)`,
          opacity: 0.75,
        },
      }}
    >
      {/* Left — logo + brand block */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ cursor: 'pointer', minWidth: 0 }}
        onClick={() => navigate('/')}
      >
        <Box
          component="img"
          src={logoUrl}
          alt="Ardinotech"
          sx={{
            height: { xs: 28, md: 36 },
            width: 'auto',
            objectFit: 'contain',
            filter: 'drop-shadow(0 2px 4px rgba(11, 21, 48, 0.12))',
          }}
        />

        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            lineHeight: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: { xs: 9, md: 10 },
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: brand.inkSoft,
              opacity: 0.85,
              ml: 0.25,
              mb: 0.25,
              whiteSpace: 'nowrap',
            }}
          >
            {ownerLabel}
          </Typography>

          <Typography
            sx={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: { xs: 18, sm: 22, md: 26 },
              fontWeight: 800,
              letterSpacing: '-0.01em',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              backgroundImage: `linear-gradient(
                100deg,
                ${brand.navy} 0%,
                ${brand.violet} 30%,
                #FFFFFF 50%,
                ${brand.violetSoft} 70%,
                ${brand.navy} 100%
              )`,
              backgroundSize: '220% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              filter: `drop-shadow(0 2px 8px ${brand.violetGlow})`,
              animation: `${shine} 4.5s ease-in-out infinite`,
            }}
          >
            {productName}
          </Typography>
        </Box>
      </Stack>

      {/* Centre — nav or tagline */}
      {center && (
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flex: 1,
            justifyContent: 'center',
          }}
        >
          {center}
        </Box>
      )}

      {/* Right — auth controls or custom node */}
      <Box>{right ?? defaultRight}</Box>
    </Box>
  );
}
